/**
 * Dispute Path Recommendation Screen
 * 
 * Shows after report analysis completes.
 * Auto-recommends either Factual or Metro 2 based on findings.
 * User can continue with recommendation or switch manually.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  ArrowLeft,
  FileText, 
  Database, 
  Check, 
  Info,
  Sparkles,
  AlertTriangle,
  Shield,
  Scale,
  ChevronRight,
  User,
  CreditCard,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

// Recommendation logic
const getRecommendation = (analysisResults) => {
  if (!analysisResults || !analysisResults.findings) {
    return { type: 'factual', reason: 'default' };
  }
  
  const { findings, summary } = analysisResults;
  
  // Check for Metro 2 indicators
  const hasFieldInconsistencies = findings.some(f => 
    f.issue_type?.includes('inconsistency') || 
    f.issue_type?.includes('conflict') ||
    f.fields_affected?.length > 0
  );
  const hasChronologyMismatches = findings.some(f => 
    f.issue_type?.includes('chronology') || 
    f.issue_type?.includes('date_mismatch')
  );
  const hasBalanceIssues = findings.some(f => 
    f.issue_type?.includes('balance') || 
    f.fields_affected?.some(field => field.includes('balance'))
  );
  const hasPaymentLogicIssues = findings.some(f => 
    f.issue_type?.includes('payment') || 
    f.issue_type?.includes('status')
  );
  
  // Check for Factual indicators
  const hasIdentityTheft = findings.some(f => 
    f.issue_type?.includes('identity') || 
    f.issue_type?.includes('fraud') ||
    f.issue_type?.includes('unauthorized')
  );
  const hasPersonalInfoIssues = findings.some(f => 
    f.issue_type?.includes('personal_info') || 
    f.issue_type?.includes('name') ||
    f.issue_type?.includes('address')
  );
  const hasSimpleErrors = findings.some(f => 
    f.issue_type?.includes('error') || 
    f.confidence_score < 0.7
  );
  
  // Calculate scores
  const metro2Score = (hasFieldInconsistencies ? 2 : 0) + 
                      (hasChronologyMismatches ? 1.5 : 0) + 
                      (hasBalanceIssues ? 1.5 : 0) + 
                      (hasPaymentLogicIssues ? 1 : 0);
  
  const factualScore = (hasIdentityTheft ? 3 : 0) + 
                       (hasPersonalInfoIssues ? 2 : 0) + 
                       (hasSimpleErrors ? 1 : 0);
  
  // Recommend based on scores
  if (metro2Score > factualScore && metro2Score >= 2) {
    return { 
      type: 'metro2', 
      reason: 'field_inconsistencies',
      indicators: { hasFieldInconsistencies, hasChronologyMismatches, hasBalanceIssues }
    };
  }
  
  return { 
    type: 'factual', 
    reason: hasIdentityTheft ? 'identity_issues' : hasPersonalInfoIssues ? 'personal_info' : 'general_errors',
    indicators: { hasIdentityTheft, hasPersonalInfoIssues, hasSimpleErrors }
  };
};

const DisputePathRecommendationScreen = ({ 
  analysisResults,
  onSelectPath,
  onBack,
}) => {
  const recommendation = getRecommendation(analysisResults);
  const [selectedPath, setSelectedPath] = useState(recommendation.type);
  
  const findingsCount = analysisResults?.findings?.length || 0;
  const accountsCount = analysisResults?.summary?.accounts_analyzed || 0;
  const inquiriesCount = analysisResults?.summary?.inquiries_analyzed || 0;
  
  const handleContinue = () => {
    onSelectPath(selectedPath);
  };
  
  const disputePaths = [
    {
      id: 'factual',
      name: 'Factual Dispute',
      icon: FileText,
      color: 'emerald',
      recommended: recommendation.type === 'factual',
      description: 'Best for users who want a clear, easy-to-understand dispute focused on errors, identity theft, unauthorized activity, and other reporting issues identified in their credit report.',
      benefits: [
        'Plain language dispute letters',
        'Focus on clear reporting errors',
        'Identity theft & unauthorized activity',
        'Great for first-time disputes',
      ],
      bestFor: [
        'Simple factual errors',
        'Unauthorized accounts/inquiries',
        'Personal information issues',
        'Identity theft cases',
      ],
    },
    {
      id: 'metro2',
      name: 'Metro 2 Dispute',
      icon: Database,
      color: 'emerald',
      recommended: recommendation.type === 'metro2',
      description: 'Best for users who want a more technical, field-specific dispute built around reporting inconsistencies, data conflicts, and Metro 2-style analysis.',
      benefits: [
        'Technical field-level analysis',
        'Metro 2 compliance focus',
        'Data consistency checks',
        'Advanced reporting logic',
      ],
      bestFor: [
        'Field-level inconsistencies',
        'Chronology mismatches',
        'Balance/status conflicts',
        'Data format violations',
      ],
    },
  ];
  
  return (
    <div className="min-h-screen bg-stone-900 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Analysis Summary */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 p-4 rounded-xl bg-slate-700/30 border border-slate-700"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Sparkles size={20} className="text-emerald-400" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Report Analysis Complete</h3>
              <p className="text-slate-400 text-sm">
                {findingsCount} potential issue{findingsCount !== 1 ? 's' : ''} found
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="text-center p-2 bg-stone-800/50 rounded-lg">
              <p className="text-2xl font-bold text-white">{accountsCount}</p>
              <p className="text-xs text-slate-400">Accounts</p>
            </div>
            <div className="text-center p-2 bg-stone-800/50 rounded-lg">
              <p className="text-2xl font-bold text-white">{inquiriesCount}</p>
              <p className="text-xs text-slate-400">Inquiries</p>
            </div>
            <div className="text-center p-2 bg-stone-800/50 rounded-lg">
              <p className="text-2xl font-bold text-white">{findingsCount}</p>
              <p className="text-xs text-slate-400">Issues</p>
            </div>
          </div>
        </motion.div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Recommended Dispute Approach</h1>
          <p className="text-slate-400">Based on our analysis, we recommend a dispute path that best fits the issues found in your report.</p>
        </div>

        {/* Dispute Path Cards */}
        <div className="mt-8 space-y-4">
          {disputePaths.map((path, index) => {
            const Icon = path.icon;
            const isSelected = selectedPath === path.id;
            const isRecommended = path.recommended;
            
            return (
              <motion.div
                key={path.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.2 }}
              >
                <button
                  onClick={() => setSelectedPath(path.id)}
                  className={`w-full text-left p-6 rounded-xl border-2 transition-all ${
                    isSelected
                      ? path.color === 'emerald'
                        ? 'border-emerald-500 bg-emerald-500/10 ring-2 ring-emerald-500/20'
                        : 'border-emerald-500 bg-emerald-500/10 ring-2 ring-emerald-500/20'
                      : 'border-slate-700 bg-stone-800/50 hover:border-slate-600'
                  }`}
                  data-testid={`dispute-path-${path.id}`}
                >
                  {/* Recommended badge */}
                  {isRecommended && (
                    <div className="flex justify-end mb-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        path.color === 'emerald' 
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-emerald-500/20 text-emerald-400'
                      }`}>
                        <Sparkles size={12} className="inline mr-1" />
                        Recommended for you
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? path.color === 'emerald'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-emerald-500 text-white'
                        : 'bg-slate-700 text-slate-400'
                    }`}>
                      <Icon size={28} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className={`text-lg font-semibold ${
                          isSelected ? 'text-white' : 'text-slate-200'
                        }`}>
                          {path.name}
                        </h3>
                        
                        {/* Selection indicator */}
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected
                            ? path.color === 'emerald'
                              ? 'border-emerald-500 bg-emerald-500'
                              : 'border-emerald-500 bg-emerald-500'
                            : 'border-slate-600'
                        }`}>
                          {isSelected && <Check size={14} className="text-white" />}
                        </div>
                      </div>
                      
                      <p className="text-slate-400 text-sm mt-2 leading-relaxed">
                        {path.description}
                      </p>
                      
                      {/* Best for section */}
                      <div className="mt-4">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Best for:</p>
                        <div className="flex flex-wrap gap-2">
                          {path.bestFor.map((item, i) => (
                            <span 
                              key={i}
                              className={`text-xs px-2 py-1 rounded ${
                                isSelected
                                  ? path.color === 'emerald'
                                    ? 'bg-emerald-500/20 text-emerald-300'
                                    : 'bg-emerald-500/20 text-emerald-300'
                                  : 'bg-slate-700 text-slate-400'
                              }`}
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Info Note */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-6 p-4 rounded-xl bg-slate-700/30 border border-slate-700"
        >
          <div className="flex items-start gap-3">
            <Info size={20} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-white font-medium text-sm">Same powerful engine, different presentation</p>
              <p className="text-slate-400 text-sm mt-1">
                Both approaches use the same dispute logic and generate equally strong letters. 
                Choose based on how you prefer to see and challenge the issues found.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8 flex flex-col sm:flex-row gap-3"
        >
          <Button
            onClick={onBack}
            variant="ghost"
            className="sm:w-auto order-2 sm:order-1"
          >
            <ArrowLeft size={18} className="mr-1" />
            Back
          </Button>
          
          <Button
            onClick={handleContinue}
            className="flex-1 order-1 sm:order-2 bg-emerald-600 hover:bg-emerald-700"
            data-testid="continue-with-path-btn"
          >
            Continue with {selectedPath === 'factual' ? 'Factual' : 'Metro 2'} Dispute
            <ArrowRight size={20} className="ml-1" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default DisputePathRecommendationScreen;
export { getRecommendation };
